import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import LoadingSpinner from '../components/LoadingSpinner';
import OnboardingWelcomeVideo from './onboarding/OnboardingWelcomeVideo';
import OnboardingStep2 from './onboarding/OnboardingStep2';
import OnboardingStep3 from './onboarding/OnboardingStep3';
import OnboardingStep4 from './onboarding/OnboardingStep4';

const Onboarding = () => {
  const navigate = useNavigate();
  const { currentUser, authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Check if user is authenticated
    if (!authLoading && !currentUser) {
      navigate('/login');
      return;
    }

    // Check if onboarding is already complete
    if (currentUser?.onboardingComplete === true) {
      navigate('/dashboard');
      return;
    }

    // Load user's current onboarding step
    if (currentUser?.uid) {
      loadOnboardingStep();
    }
  }, [currentUser, authLoading, navigate]);

  const loadOnboardingStep = async () => {
    try {
      if (!currentUser?.uid) return;

      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const step = userData?.onboardingStep ?? 0;
        setCurrentStep(step);
      } else {
        // If no user doc, start at step 0 (welcome video)
        setCurrentStep(0);
      }
    } catch (error) {
      console.error('Error loading onboarding step:', error);
      setCurrentStep(0);
    } finally {
      setLoading(false);
    }
  };

  const handleStepComplete = async (nextStep) => {
    try {
      if (!currentUser?.uid) return;

      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        onboardingStep: nextStep,
      });

      setCurrentStep(nextStep);
    } catch (error) {
      console.error('Error updating onboarding step:', error);
      // Still proceed to next step even if update fails
      setCurrentStep(nextStep);
    }
  };

  const handleCompleteOnboarding = async () => {
    try {
      if (!currentUser?.uid) return;

      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        onboardingComplete: true,
        onboardingStep: 4,
      });

      // Navigate to dashboard after completion
      navigate('/dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      // Still navigate even if update fails
      navigate('/dashboard');
    }
  };

  // Auto-skip step 1 (signup) if reached - signup already happened
  useEffect(() => {
    if (currentStep === 1) {
      handleStepComplete(2);
    }
  }, [currentStep]);

  if (authLoading || loading) {
    return <LoadingSpinner />;
  }

  // Render appropriate step based on currentStep
  switch (currentStep) {
    case 0:
      return (
        <OnboardingWelcomeVideo
          onComplete={() => handleStepComplete(1)}
          onSkip={() => handleStepComplete(2)}
        />
      );
    case 1:
      // Step 1 is signup, which already happened - auto-skip to step 2
      return <LoadingSpinner />;
    case 2:
      return (
        <OnboardingStep2
          onComplete={() => handleStepComplete(3)}
          onSkip={() => handleStepComplete(3)}
        />
      );
    case 3:
      return (
        <OnboardingStep3
          onComplete={() => handleStepComplete(4)}
          onSkip={() => handleStepComplete(4)}
        />
      );
    case 4:
      return <OnboardingStep4 onComplete={handleCompleteOnboarding} />;
    default:
      // Default to welcome video
      return (
        <OnboardingWelcomeVideo
          onComplete={() => handleStepComplete(1)}
          onSkip={() => handleStepComplete(2)}
        />
      );
  }
};

export default Onboarding;
